import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppLayout } from "../layout/Header";
import { nextStepPath, prevStepPath, preferenceProgress } from "../../js/utils/formatters";
import { useStore } from "../../js/state/store";
import { updateBuyer } from "../../js/api/buyersApi";
import { useAutosave } from "../../js/hooks/useAutosave";
import { useGuestMode } from "../../js/hooks/useGuestMode";
import { toTitleCase } from "../../js/utils/titleCase";
import GuestLockShell from "../guest/GuestLockShell";

export default function ProgressBar({ percent, label }) {
  return (
    <div data-tour="buyer-progress">
      <div className="progress-meta">
        <span>{label || "Checklist progress"}</span>
        <span>{percent}%</span>
      </div>
      <div className="progress-bar" role="progressbar" aria-valuenow={percent} aria-valuemin="0" aria-valuemax="100">
        <div className="progress-bar-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

export function ChecklistStep({ title, lead, children, preferenceKey, value }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, dispatch } = useStore();
  const { canPersist } = useGuestMode();
  const progress = preferenceProgress(state.buyer.current.preferences);

  const persist = useCallback(
    (nextValue) => {
      if (!canPersist || !preferenceKey) return;
      const buyer = state.buyer.current;
      const preferences = { ...buyer.preferences, [preferenceKey]: nextValue };
      dispatch({
        type: "UPDATE_PREFERENCES",
        payload: { [preferenceKey]: nextValue },
      });
      if (buyer.id) {
        updateBuyer(buyer.id, { ...buyer, preferences });
      }
    },
    [canPersist, dispatch, preferenceKey, state.buyer.current]
  );

  useAutosave(canPersist ? value : null, persist);

  function continueNext() {
    if (canPersist) persist(value);
    navigate(nextStepPath(location.pathname));
  }

  function goBack() {
    if (canPersist) persist(value);
    navigate(prevStepPath(location.pathname));
  }

  return (
    <AppLayout variant="buyer" showBuyerNav>
      <div className="checklist-hero" data-tour="buyer-step">
        <h1 className="section-title">{toTitleCase(title)}</h1>
        {lead ? <p className="page-lead">{lead}</p> : null}
        <div className="title-rule" />
        <div className="progress-wrap">
          <ProgressBar percent={progress.percent} />
        </div>
      </div>
      <div className="card">
        <GuestLockShell>{children}</GuestLockShell>
      </div>
      <div className="wizard-actions">
        <button type="button" className="secondary-btn" onClick={goBack}>
          Back
        </button>
        <button type="button" className="primary-btn" onClick={continueNext}>
          Continue
        </button>
      </div>
    </AppLayout>
  );
}
